// Auto-extracted tab: gap-analysis
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * GapAnalysisTab - extracted from app.jsx
 * Original lines: 10841-11152
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function GapAnalysisTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Coverage Summary Card */}
            <Card darkMode={darkMode} title="Regulatory Coverage Analysis" subtitle="Gap assessment against regulatory library">
              {gapLoading ? (
                <div className="py-12 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Calculating coverage...</div>
                </div>
              ) : gapError ? (
                <div className="py-12 text-center">
                  <div className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"} mb-4`}>{gapError}</div>
                  <Button onClick={loadGapAnalysis}>Retry</Button>
                </div>
              ) : !gapData ? (
                <div className="py-16 text-center">
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${darkMode ? "bg-purple-900/30" : "bg-gradient-to-br from-purple-100 to-violet-100"}`}>
                    <Icons.ChartBar className={`w-8 h-8 ${darkMode ? "text-purple-400" : "text-purple-500"}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${darkMode ? "text-white" : "text-slate-900"}`}>Gap Analysis Not Run</h3>
                  <p className={`mb-6 max-w-sm mx-auto ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    Run gap analysis to compare your governance objects against regulatory requirements and identify coverage gaps.
                  </p>
                  <Button variant="primary" onClick={loadGapAnalysis}>
                    <Icons.ChartBar className="w-4 h-4 mr-2" /> Run Gap Analysis
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Coverage Donut + Stats */}
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Donut Chart */}
                    <div className="relative w-40 h-40 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        {/* Background circle */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={darkMode ? "#334155" : "#e5e7eb"}
                          strokeWidth="3"
                        />
                        {/* Progress circle */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={
                            gapData.coverage.percent >= 80 ? "#10b981" : 
                            gapData.coverage.percent >= 60 ? "#f59e0b" : "#ef4444"
                          }
                          strokeWidth="3"
                          strokeDasharray={`${gapData.coverage.percent}, 100`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-4xl font-bold ${
                          gapData.coverage.percent >= 80 ? "text-emerald-600" : 
                          gapData.coverage.percent >= 60 ? "text-amber-600" : "text-red-600"
                        }`}>{gapData.coverage.percent}%</span>
                        <span className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Coverage</span>
                      </div>
                    </div>
                    
                    {/* Stats Grid */}
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className={`text-center p-4 rounded-xl ${darkMode ? "bg-slate-800" : "bg-emerald-50 border border-emerald-100"}`}>
                        <div className="text-3xl font-bold text-emerald-600">{gapData.coverage.addressed}</div>
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Addressed</div>
                      </div>
                      <div className={`text-center p-4 rounded-xl ${darkMode ? "bg-slate-800" : "bg-red-50 border border-red-100"}`}>
                        <div className="text-3xl font-bold text-red-600">{gapData.coverage.gaps_count}</div>
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Gaps</div>
                      </div>
                      <div className={`text-center p-4 rounded-xl ${darkMode ? "bg-slate-800" : "bg-blue-50 border border-blue-100"}`}>
                        <div className="text-3xl font-bold text-blue-600">{gapData.coverage.total_requirements}</div>
                        <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Total Required</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Risk Breakdown */}
                  <div className={`flex flex-wrap gap-4 p-4 rounded-xl ${darkMode ? "bg-slate-800/50" : "bg-slate-50"}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      <span className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                        Critical: <span className="font-semibold">{gapData.gaps_by_risk?.critical || 0} gaps</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                      <span className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                        High: <span className="font-semibold">{gapData.gaps_by_risk?.high || 0} gaps</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                      <span className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                        Medium: <span className="font-semibold">{gapData.gaps_by_risk?.medium || 0} gaps</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                      <span className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                        Low: <span className="font-semibold">{gapData.gaps_by_risk?.low || 0} gaps</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Regulator Breakdown */}
                  {gapData.gaps_by_regulator && Object.keys(gapData.gaps_by_regulator).length > 0 && (
                    <div className={`flex flex-wrap gap-2 pt-2`}>
                      {Object.entries(gapData.gaps_by_regulator).map(([regulator, count]) => (
                        <span 
                          key={regulator} 
                          className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700"}`}
                        >
                          {regulator}: {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
            
            {/* Attestation Blocking Warning */}
            {gapData && gapData.attestation_blocked && (
              <div className={`rounded-xl p-5 border-2 ${darkMode ? "bg-red-900/20 border-red-700/50" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? "bg-red-800/50" : "bg-red-100"}`}>
                    <Icons.Alert className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold ${darkMode ? "text-red-400" : "text-red-700"}`}>
                      Attestation Blocked
                    </h3>
                    <p className={`text-sm mt-1 ${darkMode ? "text-red-300/80" : "text-red-600"}`}>
                      Coverage must be at least {gapData.coverage_threshold}% before attestation is permitted.
                      <span className="font-semibold"> Address {gapData.requirements_to_unlock} more requirements to unlock.</span>
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => {
                          // Scroll to gaps table
                          document.getElementById("gaps-table")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        View Gaps Below
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Full Coverage Success */}
            {gapData && gapData.coverage.gaps_count === 0 && (
              <div className={`rounded-xl p-5 border-2 ${darkMode ? "bg-emerald-900/20 border-emerald-700/50" : "bg-emerald-50 border-emerald-200"}`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? "bg-emerald-800/50" : "bg-emerald-100"}`}>
                    <Icons.Check className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className={`font-bold ${darkMode ? "text-emerald-400" : "text-emerald-700"}`}>
                      Full Coverage Achieved
                    </h3>
                    <p className={`text-sm mt-1 ${darkMode ? "text-emerald-300/80" : "text-emerald-600"}`}>
                      All regulatory requirements have been addressed. You are ready for attestation.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Gaps Table */}
            {gapData && gapData.gaps?.length > 0 && (
              <Card darkMode={darkMode} title={`Regulatory Gaps (${gapData.gaps.length})`} subtitle="Click 'Adopt Rule' to add to your governance" id="gaps-table">
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <select
                    value={gapFilter.risk}
                    onChange={(e) => setGapFilter(f => ({ ...f, risk: e.target.value }))}
                    className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-200"} border`}
                  >
                    <option value="all">All Risk Levels</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <select
                    value={gapFilter.regulator}
                    onChange={(e) => setGapFilter(f => ({ ...f, regulator: e.target.value }))}
                    className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-200"} border`}
                  >
                    <option value="all">All Regulators</option>
                    {[...new Set(gapData.gaps.map(g => g.regulator))].map(reg => (
                      <option key={reg} value={reg}>{reg}</option>
                    ))}
                  </select>
                </div>
                
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${darkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Requirement</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Regulator</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Citation</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Risk</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Action</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? "divide-slate-700" : "divide-slate-100"}`}>
                      {gapData.gaps
                        .filter(g => gapFilter.risk === "all" || g.risk_level === gapFilter.risk)
                        .filter(g => gapFilter.regulator === "all" || g.regulator === gapFilter.regulator)
                        .map((gap) => (
                        <tr 
                          key={gap.id} 
                          className={`transition-colors ${darkMode ? "hover:bg-slate-800/50" : "hover:bg-slate-50"}`}
                        >
                          <td className="px-4 py-4">
                            <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{gap.title}</div>
                            <div className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{gap.category}</div>
                            {gap.steadfast_solution && (
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded ${darkMode ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"}`}>
                                  Solved by: {gap.steadfast_solution}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${darkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                              {gap.regulator}
                            </span>
                          </td>
                          <td className={`px-4 py-4 font-mono text-xs ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                            {gap.regulatory_citation || "—"}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                              gap.risk_level === "critical" ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300" :
                              gap.risk_level === "high" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300" :
                              gap.risk_level === "medium" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300" :
                              "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                            } ${darkMode && gap.risk_level === "critical" ? "bg-red-900/50 text-red-300" : ""}
                            ${darkMode && gap.risk_level === "high" ? "bg-orange-900/50 text-orange-300" : ""}
                            ${darkMode && gap.risk_level === "medium" ? "bg-yellow-900/50 text-yellow-300" : ""}`}>
                              {gap.risk_level}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <Button
                              onClick={() => adoptLibraryRule(gap)}
                              disabled={adoptingRuleId === gap.id}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {adoptingRuleId === gap.id ? (
                                <>
                                  <Icons.Clock className="w-4 h-4 animate-spin mr-1" />
                                  Adopting...
                                </>
                              ) : (
                                <>
                                  <Icons.Check className="w-4 h-4 mr-1" />
                                  Adopt Rule
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            
            {/* How It Works */}
            <Card darkMode={darkMode} title="How Gap Analysis Works">
              <div className="grid md:grid-cols-3 gap-6">
                <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50" : "bg-blue-50"}`}>
                  <div className="text-2xl mb-2">1️⃣</div>
                  <h4 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>Regulatory Library</h4>
                  <p className={`text-sm mt-1 ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                    Pre-extracted requirements from FinCEN, OFAC, OCC, Federal Reserve, NERC CIP, and FCA.
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50" : "bg-emerald-50"}`}>
                  <div className="text-2xl mb-2">2️⃣</div>
                  <h4 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>Gap Detection</h4>
                  <p className={`text-sm mt-1 ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                    We match your scenarios against the library by citation, category, and fuzzy title matching.
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50" : "bg-amber-50"}`}>
                  <div className="text-2xl mb-2">3️⃣</div>
                  <h4 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>One-Click Adoption</h4>
                  <p className={`text-sm mt-1 ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                    Click "Adopt Rule" to create a pre-filled scenario with Nine Questions already populated.
                  </p>
                </div>
              </div>
            </Card>
          </div>
    </>
  );
}

export default GapAnalysisTab;
